// API Configuration and Base Service
import { cacheService } from './cacheService';
import { performanceService } from './performanceService';
import { apiConfig } from '../config/api';
import { createLogManagementError, isLogManagementError } from '../types/logManagementErrors';
import { buildSessionExpiredLoginUrl, isAdminAppPath, type SessionExpiredReason } from '../utils/sessionExpiredRedirect';

export interface PersianMetadata {
  textDirection: 'rtl' | 'ltr';
  languageCode: string;
  recommendedFonts: string[];
  encoding: string;
  containsPersianText: boolean;
  cssProperties?: Record<string, string>;
}

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  timestamp: string;
  persianMetadata?: PersianMetadata;
}

export interface ApiError {
  message: string;
  details?: string;
  statusCode: number;
  timestamp: string;
  traceId?: string;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as ApiError).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return fallback;
}

export interface ApiServiceConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  persianSupport: boolean;
  cacheEnabled: boolean;
  cacheTTL: number;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface RequestConfig {
  timeout?: number;
  retries?: number;
  cache?: boolean;
  cacheTTL?: number;
  headers?: HeadersInit;
  /** When aborted, the request is cancelled and no retry/timeout error is shown to the user. */
  signal?: AbortSignal;
}

class ApiService {
  private token: string | null = null;
  private config: ApiServiceConfig;
  // Remove local cache in favor of centralized cache service
  private requestInterceptors: Array<(config: RequestInit) => RequestInit> = [];
  private responseInterceptors: Array<(response: Response) => Response | Promise<Response>> = [];
  private apiLoggerInitialized = false;
  private sessionRedirectInProgress = false;

  constructor(config?: Partial<ApiServiceConfig>) {
    // Use centralized API configuration
    const centralConfig = apiConfig.getConfig();

    this.config = {
      baseURL: centralConfig.baseUrl,
      timeout: centralConfig.timeout,
      retryAttempts: centralConfig.retryAttempts,
      persianSupport: true,
      cacheEnabled: true,
      cacheTTL: 300000, // 5 minutes
      ...config,
    };

    this.token = localStorage.getItem('authToken');
    this.setupDefaultInterceptors();

    // Listen for config changes
    apiConfig.onConfigChange((newConfig) => {
      this.config.baseURL = newConfig.baseUrl;
      this.config.timeout = newConfig.timeout;
      this.config.retryAttempts = newConfig.retryAttempts;
      console.log('ApiService: Configuration updated from central config:', newConfig);
    });

    // Initialize API logging after a short delay to avoid circular dependencies
    setTimeout(() => {
      this.initializeApiLogging();
    }, 100);
  }

  /**
   * Initialize API logging integration
   */
  private async initializeApiLogging(): Promise<void> {
    if (this.apiLoggerInitialized) return;

    try {
      // Dynamically import ApiLogger to avoid circular dependencies
      const { apiLogger } = await import('./apiLogger');

      console.log('ApiService: Initializing API logging integration...');

      // Initialize the API logger
      apiLogger.initialize();

      this.apiLoggerInitialized = true;
      console.log('ApiService: API logging integration initialized successfully');
    } catch (error) {
      console.error('ApiService: Failed to initialize API logging:', error);
    }
  }

  private setupDefaultInterceptors() {
    // Request interceptor for admin token validation and restoration
    // Requirement 1.4: Automatically restore token to API service
    // IMPORTANT: For admin endpoints, always use admin token (even if user token is set)
    this.addRequestInterceptor((config) => {
      const requestUrl = (config as any).url || '';
      const isAdminEndpoint = requestUrl.includes('/admin/') || 
                             requestUrl.includes('/api/LogManagement/') ||
                             requestUrl.includes('/api/Admin/') ||
                             requestUrl.includes('/api/Comment/user/') || // Admin-only: getUserComments, getUserUnpublishedCommentCount
                             requestUrl.includes('/api/Comment/admin') || // Admin-only: getCommentsWithFilter
                             (requestUrl.includes('/api/Comment/') && requestUrl.includes('/approval')); // Admin-only: updateCommentApproval
      
      // For admin endpoints, always ensure we use the admin token (not user token)
      if (isAdminEndpoint) {
        console.log('ApiService: Admin endpoint detected, checking admin token');
        
        // Try to restore/use admin token from localStorage for admin endpoints
        try {
          const adminToken = localStorage.getItem('adminToken');
          if (adminToken) {
            const adminTokenInfo = localStorage.getItem('adminTokenInfo');
            if (adminTokenInfo) {
              const tokenInfo = JSON.parse(adminTokenInfo);
              const expirationTime = new Date(tokenInfo.expiresAt).getTime();
              
              // Only use if not expired
              if (Date.now() < expirationTime) {
                console.log('ApiService: Using admin token for admin endpoint');
                this.token = adminToken;
              } else {
                console.log('ApiService: Admin token expired, not using');
                this.token = null; // Clear token if expired
              }
            } else if (adminToken) {
              // Admin token exists but no info - use it anyway
              console.log('ApiService: Using admin token (no expiration info)');
              this.token = adminToken;
            }
          } else {
            console.warn('ApiService: Admin endpoint but no admin token found');
            this.token = null;
          }
        } catch (error) {
          console.warn('ApiService: Failed to set admin token in interceptor:', error);
        }
      }
      
      return config;
    });

    // Request interceptor for token management
    this.addRequestInterceptor((config) => {
      const requestUrl = (config as any).url || '';
      if (this.isPublicAuthEndpoint(requestUrl)) {
        return config;
      }
      if (this.token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${this.token}`,
        };
      }
      return config;
    });

    // Enhanced LogManagement-specific request interceptor
    this.addRequestInterceptor((config) => {
      // Add special headers for LogManagement endpoints
      const requestUrl = (config as any).url || '';
      if (requestUrl.includes('/api/LogManagement/')) {
        // Only add basic headers to avoid CORS issues
        config.headers = {
          ...config.headers,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        };

        // Only add LogManagement headers if API logging is not disabled
        if (localStorage.getItem('disableApiLogging') !== 'true') {
          config.headers = {
            ...config.headers,
            'X-LogManagement-Client': 'AdminPanel',
            'X-Request-Source': 'LogManagement',
            'X-LogManagement-Version': '1.0'
          };
        }

        // Ensure admin token is used for LogManagement endpoints
        // Use this.token which should already be set to admin token by previous interceptor
        // But fallback to localStorage if somehow not set
        const tokenToUse = this.token || localStorage.getItem('adminToken');
        if (tokenToUse) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${tokenToUse}`
          };
          // Also ensure this.token is set for consistency
          if (!this.token) {
            this.token = tokenToUse;
          }
        }
      }
      return config;
    });

    // Response interceptor for Persian metadata processing
    this.addResponseInterceptor(async (response) => {
      if (this.config.persianSupport && response.ok) {
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json();
          if (data.persianMetadata) {
            this.processPersianMetadata(data.persianMetadata);
          }
        } catch {
          // Ignore JSON parsing errors for non-JSON responses
        }
      }
      return response;
    });

    // Enhanced LogManagement-specific response interceptor
    this.addResponseInterceptor(async (response) => {
      if (response.url && response.url.includes('/api/LogManagement/')) {
        if (!response.ok) {
          if (response.status === 401) {
            // Dispatch LogManagement-specific auth failure event
            window.dispatchEvent(new CustomEvent('logmanagement:auth-failed', {
              detail: {
                error: { statusCode: response.status, message: 'LogManagement authentication failed' },
                endpoint: response.url
              }
            }));
          } else if (response.status === 503) {
            // Service unavailable
            window.dispatchEvent(new CustomEvent('logmanagement:service-unavailable', {
              detail: {
                error: { statusCode: response.status, message: 'LogManagement service unavailable' },
                endpoint: response.url
              }
            }));
          } else if (response.status === 404) {
            // Endpoint not found
            window.dispatchEvent(new CustomEvent('logmanagement:endpoint-not-found', {
              detail: {
                error: { statusCode: response.status, message: 'LogManagement endpoint not found' },
                endpoint: response.url
              }
            }));
          }
        }
      }
      return response;
    });
  }

  private processPersianMetadata(metadata: PersianMetadata) {
    // Temporarily disabled CSS injection to prevent PostCSS errors
    // if (metadata.cssProperties) {
    //   // Apply global Persian CSS properties if needed
    //   const style = document.createElement('style');
    //   style.textContent = Object.entries(metadata.cssProperties)
    //     .map(([selector, properties]) => `${selector} { ${properties} }`)
    //     .join('\n');
    //   document.head.appendChild(style);
    // }

    // Load recommended fonts
    if (metadata.recommendedFonts?.length > 0) {
      this.loadPersianFonts(metadata.recommendedFonts);
    }
  }

  private async loadPersianFonts(_fonts: string[]) {
    // Persian UI fonts are bundled with the app (@fontsource). Do not inject third-party font stylesheets.
    await Promise.resolve();
  }

  addRequestInterceptor(interceptor: (config: RequestInit) => RequestInit) {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: (response: Response) => Response | Promise<Response>) {
    this.responseInterceptors.push(interceptor);
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  // Admin-specific token management methods to avoid conflicts with regular user auth
  setAdminToken(token: string): void {
    console.log('ApiService: setAdminToken called with token:', token ? 'Token provided' : 'No token');
    this.token = token;
    console.log('ApiService: Admin token set successfully');
    // Don't store admin token as authToken to avoid conflicts with regular user authentication
    
    // Dispatch event for token update
    window.dispatchEvent(new CustomEvent('admin:token-updated', {
      detail: { token, timestamp: Date.now() }
    }));
  }

  clearAdminToken(): void {
    console.log('ApiService: Clearing admin token');
    this.token = null;
    // Don't remove authToken from localStorage as it might belong to regular user
    
    // Dispatch event for token cleared
    window.dispatchEvent(new CustomEvent('admin:token-cleared', {
      detail: { timestamp: Date.now() }
    }));
  }

  /**
   * Centralized handler to redirect users when the server reports an expired/invalid session.
   * Redirects immediately, message will be shown on login page.
   */
  private triggerSessionTimeoutRedirect(target: 'admin' | 'user', reason: SessionExpiredReason = 'session_expired') {
    // `__sessionRedirectInProgress` is set by listeners before they navigate.
    // Only skip when navigation has actually started.
    if ((window as any).__sessionRedirectStarted) {
      console.log('ApiService: Session redirect already started, skipping');
      return;
    }

    if (this.isIntentionalLogoutInProgress()) {
      console.log('ApiService: Intentional logout in progress — skipping session-expired redirect');
      return;
    }

    const loginPath = target === 'admin' ? '/admin/login' : '/login';
    const currentPathname = window.location.pathname;
    if (currentPathname === '/login' || currentPathname === '/admin/login') {
      console.log('ApiService: Already on login page, skipping session redirect');
      return;
    }

    this.sessionRedirectInProgress = true;
    (window as any).__sessionRedirectInProgress = true;
    (window as any).__sessionRedirectStarted = true;

    const redirectUrl = buildSessionExpiredLoginUrl(target, reason);

    // Redirect immediately using replace to prevent back button issues
    // Use synchronous redirect to prevent page freezing
    try {
      window.location.replace(redirectUrl);
    } catch (error) {
      // Fallback if replace fails
      console.error('ApiService: Failed to redirect using replace, trying href:', error);
      window.location.href = redirectUrl;
    }
    
    // Don't reset sessionRedirectInProgress since we're navigating away
  }

  /**
   * Automatically restore admin token from storage to API service
   * Requirement 1.4: If token is valid but not set in API service, restore it
   */
  async restoreAdminToken(): Promise<boolean> {
    try {
      console.log('ApiService: Attempting to restore admin token from storage');
      
      // Import adminTokenStorage dynamically to avoid circular dependencies
      const { adminTokenStorage } = await import('./adminTokenStorage');
      
      const token = adminTokenStorage.getToken();
      if (!token) {
        console.log('ApiService: No admin token available to restore');
        return false;
      }

      // Expired JWTs are still restored so refresh can renew them.
      if (this.token === token) {
        console.log('ApiService: Admin token already set in API service');
        return true;
      }
      
      this.setAdminToken(token);
      console.log('ApiService: Admin token restored successfully');
      
      return true;
      
    } catch (error) {
      console.error('ApiService: Failed to restore admin token:', error);
      return false;
    }
  }

  /**
   * Validate admin token before making requests
   * Requirement 2.4: Ensure all API calls have valid authentication
   */
  async validateAdminToken(): Promise<boolean> {
    try {
      console.log('ApiService: Validating admin token');
      
      // Import adminTokenStorage dynamically
      const { adminTokenStorage } = await import('./adminTokenStorage');
      
      const token = adminTokenStorage.getToken();
      if (!token) {
        console.log('ApiService: Admin token validation failed: No token found');
        this.clearAdminToken();
        window.dispatchEvent(new CustomEvent('admin:token-validation-failed', {
          detail: {
            reason: 'No token found',
            isExpired: false,
            timestamp: Date.now()
          }
        }));
        return false;
      }

      const validation = adminTokenStorage.validateToken();
      // Extend only while the admin is still inside the Token Expiration idle window.
      if (validation.isExpired || adminTokenStorage.isTokenExpiringSoon()) {
        const { adminMaySilentlyExtendSession, enforceAdminIdleTimeout } = await import('../utils/sessionExpirationPolicy');
        if (!adminMaySilentlyExtendSession()) {
          await enforceAdminIdleTimeout();
          return false;
        }
        const refreshed = await this.refreshAdminToken();
        if (refreshed.success && refreshed.token) {
          console.log('ApiService: Admin token renewed during validation');
          return true;
        }
        // Keep stored token; API 401 path will retry refresh / redirect if needed.
        this.setAdminToken(token);
        return true;
      }

      if (this.token !== token) {
        console.log('ApiService: Updating API service with validated token');
        this.setAdminToken(token);
      }
      
      console.log('ApiService: Admin token validation successful');
      return true;
      
    } catch (error) {
      console.error('ApiService: Admin token validation error:', error);
      return false;
    }
  }

  /**
   * Ensure admin token is valid and set before making admin requests
   * Automatically restores token if needed
   */
  async ensureAdminToken(): Promise<boolean> {
    try {
      // Check if we already have a token set
      if (this.token) {
        // Validate it
        const isValid = await this.validateAdminToken();
        if (isValid) {
          return true;
        }
      }
      
      // Try to restore token from storage
      const restored = await this.restoreAdminToken();
      
      if (!restored) {
        console.log('ApiService: Unable to ensure admin token');
        return false;
      }
      
      return true;
      
    } catch (error) {
      console.error('ApiService: Failed to ensure admin token:', error);
      return false;
    }
  }

  /**
   * Refresh LogManagement admin token
   * Enhanced with better error handling and token persistence integration
   */
  private async refreshLogManagementToken(options?: { force?: boolean }): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      console.log('API: Attempting to refresh LogManagement admin token...');

      // Check if we have a valid admin token to refresh using storage service
      let currentAdminToken: string | null = null;
      let tokenInfo: any = null;
      
      try {
        const { adminTokenStorage } = require('./adminTokenStorage');
        currentAdminToken = adminTokenStorage.getToken();
        tokenInfo = adminTokenStorage.getTokenInfo();
      } catch (error) {
        console.warn('API: Failed to get admin token from storage service, falling back to localStorage');
        currentAdminToken = localStorage.getItem('adminToken');
        const adminTokenInfo = localStorage.getItem('adminTokenInfo');
        
        if (adminTokenInfo) {
          try {
            tokenInfo = JSON.parse(adminTokenInfo);
          } catch {
            console.log('API: Invalid admin token info format');
            return { success: false, error: 'Invalid token info' };
          }
        }
      }

      if (!currentAdminToken) {
        console.log('API: No admin token available for refresh');
        return { success: false, error: 'No admin token available' };
      }

      const expirationTime = tokenInfo?.expiresAt
        ? new Date(tokenInfo.expiresAt).getTime()
        : 0;
      const timeUntilExpiry = expirationTime ? expirationTime - Date.now() : 0;
      // force=true after 401; otherwise refresh when expired/near expiry
      const shouldRefresh = !!options?.force || !expirationTime || timeUntilExpiry < 5 * 60 * 1000;

      if (!shouldRefresh) {
        this.setAdminToken(currentAdminToken);
        return { success: true, token: currentAdminToken };
      }

      console.log('API: Admin token near/past expiry, attempting refresh...');

      const refreshController = new AbortController();
      const refreshTimeoutId = setTimeout(() => refreshController.abort(), 10000);

      try {
        const refreshResponse = await fetch(`${this.config.baseURL}/api/admin/refresh-token`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentAdminToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token: currentAdminToken }),
          signal: refreshController.signal
        });
        clearTimeout(refreshTimeoutId);

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();

          if (refreshData.token) {
            try {
              const { adminTokenStorage } = require('./adminTokenStorage');
              adminTokenStorage.storeToken(refreshData.token, refreshData.expiresAt, tokenInfo?.user);
            } catch (error) {
              console.warn('API: Failed to store refreshed token using storage service, falling back to localStorage');
              localStorage.setItem('adminToken', refreshData.token);
              localStorage.setItem('adminTokenInfo', JSON.stringify({
                token: refreshData.token,
                expiresAt: refreshData.expiresAt,
                user: tokenInfo?.user
              }));
            }

            this.setAdminToken(refreshData.token);

            window.dispatchEvent(new CustomEvent('admin:token-refreshed', {
              detail: { 
                token: refreshData.token,
                expiresAt: refreshData.expiresAt,
                timestamp: Date.now()
              }
            }));

            console.log('API: Admin token refresh successful');
            return { success: true, token: refreshData.token };
          }
        }

        console.log('API: Admin token refresh failed with status:', refreshResponse.status);

        // Keep a not-yet-expired token if refresh is unavailable
        if (timeUntilExpiry > 0) {
          console.warn('API: Refresh unavailable; continuing with current admin token');
          this.setAdminToken(currentAdminToken);
          return { success: true, token: currentAdminToken };
        }

        window.dispatchEvent(new CustomEvent('admin:token-refresh-failed', {
          detail: {
            status: refreshResponse.status,
            timestamp: Date.now()
          }
        }));

        return { success: false, error: `Refresh failed: ${refreshResponse.status}` };
      } catch (refreshError) {
        clearTimeout(refreshTimeoutId);
        console.error('API: Refresh token error:', refreshError);

        if (timeUntilExpiry > 0) {
          console.warn('API: Refresh error; continuing with current admin token');
          this.setAdminToken(currentAdminToken);
          return { success: true, token: currentAdminToken };
        }

        window.dispatchEvent(new CustomEvent('admin:session-expired', {
          detail: {
            reason: 'refresh_error',
            error: refreshError,
            timestamp: Date.now()
          }
        }));

        return { success: false, error: 'Refresh failed' };
      }
    } catch (error) {
      console.error('API: LogManagement token refresh error:', error);
      
      // Dispatch refresh exception event
      window.dispatchEvent(new CustomEvent('admin:token-refresh-error', {
        detail: { 
          error,
          timestamp: Date.now()
        }
      }));
      
      return { success: false, error: 'Token refresh exception' };
    }
  }

  /**
   * Public method to refresh admin token
   * Requirement: Implement token validation interceptors
   */
  async refreshAdminToken(): Promise<{ success: boolean; token?: string; error?: string }> {
    const { adminMaySilentlyExtendSession, enforceAdminIdleTimeout } = await import('../utils/sessionExpirationPolicy');
    if (!adminMaySilentlyExtendSession()) {
      await enforceAdminIdleTimeout();
      return { success: false, error: 'Admin session expired' };
    }
    return await this.refreshLogManagementToken({ force: true });
  }

  private getCacheKey(method: string, url: string, data?: any): string {
    const params = data ? { body: data } : {};
    return cacheService.createApiKey(`${method}:${url}`, params);
  }

  /** Login/register/captcha — must not send stale tokens or run session-timeout redirects. */
  private isPublicAuthEndpoint(urlOrEndpoint: string): boolean {
    const lower = urlOrEndpoint.toLowerCase();
    return (
      lower.includes('/auth/login') ||
      lower.includes('/auth/register') ||
      lower.includes('/auth/forgot-password') ||
      lower.includes('/auth/reset-password') ||
      lower.includes('/auth/refresh') ||
      lower.includes('/admin/refresh-token') ||
      lower.includes('/auth/external-auth-providers') ||
      lower.includes('/admin/login') ||
      lower.includes('/logincaptcha') ||
      lower.includes('/auth/two-factor/login') ||
      lower.includes('/auth/two-factor/setup-login')
    );
  }

  /** True while the user is signing out on purpose — never treat as session expiry. */
  private isIntentionalLogoutInProgress(): boolean {
    return !!(window as any).__intentionalLogoutInProgress;
  }

  private getHeaders(isFormData: boolean = false): HeadersInit {
    const headers: HeadersInit = {
      'Accept': 'application/json',
      'Accept-Language': 'en,fa',
    };

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    // Always get fresh token from localStorage for user endpoints
    // (Admin endpoints handle token in interceptor)
    const requestUrl = (this as any).currentRequestUrl || '';
    const isAdminEndpoint = requestUrl.includes('/admin/') || 
                           requestUrl.includes('/api/LogManagement/') ||
                           requestUrl.includes('/api/Admin/') ||
                           requestUrl.includes('/api/Comment/user/') || // Admin-only: getUserComments, getUserUnpublishedCommentCount
                           requestUrl.includes('/api/Comment/admin') || // Admin-only: getCommentsWithFilter
                           (requestUrl.includes('/api/Comment/') && requestUrl.includes('/approval')); // Admin-only: updateCommentApproval
    
    let tokenToUse = this.token;
    
    // For non-admin endpoints, always check localStorage for fresh token
    // This ensures we use the latest token even if it was updated after ApiService initialization
    if (!isAdminEndpoint) {
      const freshToken = localStorage.getItem('authToken');
      if (freshToken) {
        tokenToUse = freshToken;
        // Update cached token for consistency
        if (this.token !== freshToken) {
          this.token = freshToken;
          console.log('ApiService: Updated token from localStorage');
        }
      }
    }

    if (tokenToUse && !this.isPublicAuthEndpoint(requestUrl)) {
      headers.Authorization = `Bearer ${tokenToUse}`;
      console.log('ApiService: Authorization header added with token');
    } else if (!this.isPublicAuthEndpoint(requestUrl) && !tokenToUse) {
      console.warn('ApiService: No token available for request to:', requestUrl);
    }

    if (!isAdminEndpoint) {
      try {
        const storedLocale = localStorage.getItem('persianist_locale') || 'en';
        headers['X-UI-Language'] = storedLocale.toLowerCase().startsWith('fa') ? 'fa' : 'en';
      } catch {
        headers['X-UI-Language'] = 'en';
      }
    }

    return headers;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    // Apply response interceptors
    let processedResponse = response;
    for (const interceptor of this.responseInterceptors) {
      processedResponse = await interceptor(processedResponse);
    }

    if (!processedResponse.ok) {
      await this.handleErrorResponse(processedResponse);
    }

    // Handle 204 NoContent responses (no body)
    if (processedResponse.status === 204) {
      console.log('API: 204 NoContent response, returning empty result');
      return {} as T;
    }

    // Check if response has content
    const contentType = processedResponse.headers.get('content-type');
    const hasContent = contentType && contentType.includes('application/json');
    
    if (!hasContent) {
      // No JSON content, return empty object
      return {} as T;
    }

    const responseData = await processedResponse.json();
    console.log('API: Response data:', responseData);
    console.log('API: Response type:', typeof responseData);
    console.log('API: Is array:', Array.isArray(responseData));
    return responseData;
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    let errorData: any = {};

    try {
      // Clone response to avoid consuming the stream
      const responseClone = response.clone();
      const text = await responseClone.text();

      if (text) {
        try {
          errorData = JSON.parse(text);
        } catch {
          // If not JSON, use text as message
          errorData = {
            message: text || response.statusText || 'An error occurred',
            details: `HTTP ${response.status}`,
          };
        }
      } else {
        errorData = {
          message: response.statusText || 'An error occurred',
          details: `HTTP ${response.status}`,
        };
      }
    } catch {
      // If response is not readable, create a generic error
      errorData = {
        message: response.statusText || 'Network error occurred',
        details: `HTTP ${response.status}`,
      };
    }

    const messageText =
      (typeof errorData?.message === 'string' && errorData.message) ||
      (typeof errorData?.Message === 'string' && errorData.Message) ||
      (typeof errorData?.title === 'string' && errorData.title) ||
      undefined;
    const detailText =
      (typeof errorData?.detail === 'string' && errorData.detail) ||
      (typeof errorData?.Detail === 'string' && errorData.Detail) ||
      undefined;
    const serverText =
      messageText && detailText && !messageText.includes(detailText)
        ? `${messageText} (${detailText})`
        : messageText || detailText;

    if (response.status === 413) {
      const { payloadTooLargeMessage } = await import('../utils/mediaUploadLimits');
      const payloadError: ApiError = {
        message:
          serverText && /media upload|too large|413/i.test(serverText)
            ? serverText
            : payloadTooLargeMessage(),
        statusCode: 413,
        timestamp: new Date().toISOString(),
        traceId: errorData.traceId
      };
      throw payloadError;
    }

    const apiError: ApiError = {
      message: this.getErrorMessage(response.status, serverText),
      details: errorData.details,
      statusCode: response.status,
      timestamp: new Date().toISOString(),
      traceId: errorData.traceId,
    };

    (apiError as ApiError & { responseData?: unknown }).responseData = errorData;

    // Add validation errors if present
    if (errorData.errors) {
      (apiError as any).errors = errorData.errors;
    }

    // Add request URL for better error context
    (apiError as any).config = { url: response.url };

    // Enhanced LogManagement-specific error handling
    const isLogManagementEndpoint = response.url && response.url.includes('/api/LogManagement/');

    if (isLogManagementEndpoint) {
      // Create specialized LogManagement error
      const logManagementError = createLogManagementError(
        response.status,
        apiError.message,
        apiError,
        response.url
      );

      // Throw the specialized error instead of generic API error
      throw logManagementError;
    }

    // Handle specific error cases
    if (response.status === 401) {
      const responseUrl = response.url || '';
      const urlLower = responseUrl.toLowerCase();
      const isAdminEndpoint = response.url && (
        response.url.includes('/admin/') || 
        response.url.includes('/api/Admin/') ||
        response.url.includes('/api/Comment/user/') || // Admin-only comment endpoints
        response.url.includes('/api/Comment/admin') ||
        (response.url.includes('/api/Comment/') && response.url.includes('/approval')) ||
        isLogManagementEndpoint
      );
      const isAuthEndpoint = urlLower.includes('/auth/login') ||
        urlLower.includes('/auth/register') ||
        urlLower.includes('/auth/logout') ||
        urlLower.includes('/admin/login');
      
      if (isAuthEndpoint || this.isIntentionalLogoutInProgress()) {
        // Let login/register/logout flows surface their own errors without session-expired redirects
        console.log('API: Auth/logout endpoint returned 401 - skipping global redirect');
      } else if (isAdminEndpoint) {
        // Only force admin login when there is no stored admin session left
        // (refresh + retry already ran in makeRequest before handleResponse).
        const { adminTokenStorage } = await import('./adminTokenStorage');
        const storedToken = adminTokenStorage.getToken();
        if (storedToken) {
          const { adminIdleLimitExceeded, enforceAdminIdleTimeout, redirectAdminForSecurity } = await import('../utils/sessionExpirationPolicy');
          if (adminIdleLimitExceeded()) {
            await enforceAdminIdleTimeout();
          } else if (isAdminAppPath()) {
            console.warn('ApiService: Admin authentication rejected — redirecting to admin login');
            await redirectAdminForSecurity('session_expired');
          } else {
            throw apiError;
          }
          const redirectError = new Error('Session expired - redirecting to login') as any;
          redirectError.statusCode = 401;
          redirectError.redirected = true;
          redirectError.isSessionExpired = true;
          throw redirectError;
        }

        // No admin token (or not on admin UI) — do not bounce learners to admin login
        if (!isAdminAppPath()) {
          console.log('ApiService: Admin endpoint 401 outside admin UI — surfacing error without redirect');
          throw apiError;
        }

        console.log('ApiService: Admin authentication error detected (401) - redirecting to admin login');

        this.clearAdminToken();
        try {
          const { adminService } = await import('./adminService');
          adminService.clearAdminSession();
        } catch (clearError) {
          console.warn('ApiService: Failed to clear admin session from storage:', clearError);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenInfo');
        }
        
        if (!apiError.statusCode) {
          (apiError as any).statusCode = 401;
        }
        if (!apiError.status) {
          (apiError as any).status = 401;
        }
        
        window.dispatchEvent(new CustomEvent('admin:auth-failed', {
          detail: { 
            error: apiError,
            statusCode: 401,
            endpoint: response.url,
            requiresLogin: true,
            timestamp: Date.now()
          }
        }));
        
        if (isLogManagementEndpoint) {
          window.dispatchEvent(new CustomEvent('logmanagement:auth-failed', {
            detail: { error: apiError, endpoint: response.url }
          }));
        }

        this.triggerSessionTimeoutRedirect('admin');
        
        const redirectError = new Error('Session expired - redirecting to login') as any;
        redirectError.statusCode = 401;
        redirectError.redirected = true;
        redirectError.isSessionExpired = true;
        throw redirectError;
      } else {
        if (isAdminAppPath()) {
          const { enforceAdminIdleTimeout, adminIdleLimitExceeded, redirectAdminForSecurity } = await import('../utils/sessionExpirationPolicy');
          console.log('ApiService: Admin session rejected — redirecting to admin login');
          if (adminIdleLimitExceeded()) {
            await enforceAdminIdleTimeout();
          } else if (!localStorage.getItem('adminToken') || !(await redirectAdminForSecurity('session_expired'))) {
            if (!(window as any).__sessionRedirectStarted) {
              this.triggerSessionTimeoutRedirect('admin');
            }
          }
          const redirectError = new Error('Session expired - redirecting to login') as any;
          redirectError.statusCode = 401;
          redirectError.redirected = true;
          redirectError.isSessionExpired = true;
          throw redirectError;
        }

        const { learnerMayExtendSession, redirectLearnerForSecurity } = await import('../utils/sessionExpirationPolicy');
        const refreshRejected = (response.url || '').toLowerCase().includes('/auth/refresh');
        if (learnerMayExtendSession() && !refreshRejected) {
          console.log('ApiService: Learner 401 — extending session, not signing out for idle time');
          throw apiError;
        }

        console.log('ApiService: Learner security failure — redirecting to login');
        redirectLearnerForSecurity();

        window.dispatchEvent(new CustomEvent('auth:unauthorized', {
          detail: { error: apiError, reason: 'server_session_timeout' }
        }));
        window.dispatchEvent(new CustomEvent('session:expired', {
          detail: { error: apiError, reason: 'token_expired_or_invalid' }
        }));

        const redirectError = new Error('Session expired - redirecting to login') as any;
        redirectError.statusCode = 401;
        redirectError.redirected = true;
        redirectError.isSessionExpired = true;
        throw redirectError;
      }
    }

    // Dispatch global error event for centralized handling
    window.dispatchEvent(new CustomEvent('api:error', {
      detail: { error: apiError, response }
    }));

    throw apiError;
  }

  private getErrorMessage(statusCode: number, originalMessage?: string): string {
    if (statusCode === 413) {
      return originalMessage && !/^an unexpected error occurred$/i.test(originalMessage)
        ? originalMessage
        : 'The request was rejected because the upload exceeds Media Upload settings (HTTP 413). Check Admin → System configuration → Media Upload.';
    }

    const errorMessages: Record<number, string> = {
      400: 'Bad request - please check your input',
      401: 'Please log in to access this resource',
      403: 'You do not have permission to access this resource',
      404: 'The requested resource was not found',
      408: 'Request timeout - please try again',
      429: 'Too many requests - please wait and try again',
      500: 'Internal server error - please try again later',
      502: 'Server is unavailable',
      503: 'Service temporarily unavailable',
      504: 'Gateway timeout - please try again',
    };

    return originalMessage || errorMessages[statusCode] || 'An unexpected error occurred';
  }

  private async makeRequest<T>(
    method: string,
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    // Learner activity timestamp only. Admin idle time is tracked in sessionExpirationPolicy.
    if (!this.isPublicAuthEndpoint(endpoint) && !isAdminAppPath()) {
      void import('../utils/sessionInactivityTracker')
        .then(({ sessionInactivityTracker }) => sessionInactivityTracker.recordActivity())
        .catch(() => {});
    }

    const url = `${this.config.baseURL}${endpoint}`;
    console.log(`API: Making ${method} request to ${url}`);
    console.log('API: Request data:', data);

    const cacheKey = this.getCacheKey(method, url, data);
    const isLogManagementEndpoint = endpoint.includes('/api/LogManagement/');
    const requestStartTime = Date.now();

    // Check cache for GET requests
    if (method === 'GET' && config?.cache !== false) {
      const cached = cacheService.get<T>(cacheKey);
      if (cached) return cached;
    }

    // Check if data is FormData
    const isFormData = data instanceof FormData;

    // Set current request URL for getHeaders to determine endpoint type
    (this as any).currentRequestUrl = url;

    // Get base headers (already returns a plain object)
    const baseHeaders = this.getHeaders(isFormData) as Record<string, string>;
    
    // Clear current request URL after headers are set
    delete (this as any).currentRequestUrl;

    // Convert config headers to plain object if needed
    let configHeadersObj: Record<string, string> = {};
    if (config?.headers) {
      const configHeaders = config.headers;
      if (configHeaders instanceof Headers) {
        configHeadersObj = Object.fromEntries(configHeaders.entries());
      } else if (Array.isArray(configHeaders)) {
        configHeadersObj = Object.fromEntries(configHeaders);
      } else {
        configHeadersObj = configHeaders as Record<string, string>;
      }
    }

    // Merge headers, ensuring all values are strings
    const finalHeaders: Record<string, string> = { ...baseHeaders };
    for (const [key, value] of Object.entries(configHeadersObj)) {
      if (value != null && value !== undefined) {
        finalHeaders[key] = String(value);
      }
    }

    const requestConfig: RequestInit = {
      method,
      headers: finalHeaders,
      body: isFormData ? data : (data ? JSON.stringify(data) : null),
      mode: 'cors', // Explicitly set CORS mode
      credentials: 'omit', // Don't send credentials to avoid CORS issues
    };
    (requestConfig as any).url = url;

    console.log('API: Request config:', {
      method: requestConfig.method,
      headers: requestConfig.headers,
      body: requestConfig.body
    });

    // Apply request interceptors
    let processedConfig = requestConfig;
    for (const interceptor of this.requestInterceptors) {
      processedConfig = interceptor(processedConfig);
    }

    // Add API logging metadata to config for interceptor
    (processedConfig as any)._apiLoggerStartTime = requestStartTime;
    (processedConfig as any)._apiLoggerEndpoint = endpoint;
    (processedConfig as any)._apiLoggerMethod = method;
    (processedConfig as any).url = url;

    const maxRetries = config?.retries ?? this.config.retryAttempts;
    const timeout = config?.timeout ?? this.config.timeout;

    console.log(`API: Using timeout: ${timeout}ms, max retries: ${maxRetries}`);

    let lastError: Error;
    let tokenRefreshAttempted = false;
    let authErrorDetected = false;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // If caller aborted (e.g. navigation), don't start or retry
        if (config?.signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }

        console.log(`API: Attempt ${attempt + 1}/${maxRetries + 1}`);

        const controller = new AbortController();
        let timeoutId: NodeJS.Timeout | null = null;
        let timeoutFired = false;
        let externalAbortListener: (() => void) | null = null;

        // Set up timeout with better tracking
        timeoutId = setTimeout(() => {
          console.log(`API: Request timed out after ${timeout}ms, aborting...`);
          timeoutFired = true;
          controller.abort();
        }, timeout);

        // When caller aborts (e.g. lesson change or Strict Mode cleanup), cancel this request
        if (config?.signal) {
          externalAbortListener = () => controller.abort();
          config.signal.addEventListener('abort', externalAbortListener);
        }

        const clearExternalAbort = () => {
          if (externalAbortListener && config?.signal) {
            config.signal.removeEventListener('abort', externalAbortListener);
            externalAbortListener = null;
          }
        };

        let response: Response;
        const fetchStartTime = Date.now();
        try {
          console.log(`API: Sending fetch request... (timeout: ${timeout}ms)`);
          response = await fetch(url, {
            ...processedConfig,
            signal: controller.signal,
          });
          const fetchDuration = Date.now() - fetchStartTime;
          console.log(`API: Fetch resolved after ${fetchDuration}ms`);
          clearExternalAbort();
        } catch (fetchError: any) {
          const fetchDuration = Date.now() - fetchStartTime;
          console.log(`API: Fetch failed after ${fetchDuration}ms:`, fetchError.name, fetchError.message);
          clearExternalAbort();

          // Clear timeout if it hasn't fired yet
          if (timeoutId && !timeoutFired) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          // Handle AbortError (timeout or manual abort)
          if (fetchError.name === 'AbortError' || controller.signal.aborted || timeoutFired) {
            console.error(`API: Request was aborted (timeoutFired: ${timeoutFired}, aborted: ${controller.signal.aborted})`);
            // Caller-initiated abort (e.g. navigation) – throw AbortError so UI can ignore
            if (config?.signal?.aborted) {
              throw new DOMException('Aborted', 'AbortError');
            }
            const isLoginEndpoint = url.includes('/auth/login') || url.includes('/Auth/login');
            if (isLoginEndpoint) {
              throw new Error(`Login request timed out after ${timeout}ms. Please check your connection and try again.`);
            } else {
              throw new Error('Request timed out - please check your connection and try again');
            }
          }

          // Re-throw other errors
          throw fetchError;
        }

        // Clear timeout IMMEDIATELY after response is received to prevent race condition
        if (timeoutId && !timeoutFired) {
          clearTimeout(timeoutId);
          timeoutId = null;
        } else if (timeoutFired) {
          console.warn('API: Warning - timeout fired but fetch still resolved. This should not happen.');
          // If timeout fired but fetch resolved, the response might be incomplete
          // We should still try to process it, but log a warning
        }

        console.log('API: Received response:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });

        // Check for authentication errors BEFORE processing response
        if (!response.ok) {
          const isAuthError = response.status === 401 || response.status === 403;
          const isAuthEndpoint = url.includes('/auth/') || url.includes('/Auth/') || url.includes('/admin/login');
          const isRefreshEndpoint = url.toLowerCase().includes('/auth/refresh');
          const isAdminEndpoint =
            url.toLowerCase().includes('/api/admin/') ||
            url.toLowerCase().includes('/admin/') ||
            url.toLowerCase().includes('/logmanagement/');

          console.log(`API: Error response debug - URL: ${url}, Status: ${response.status}, IsAuthError: ${isAuthError}, IsAuthEndpoint: ${isAuthEndpoint}, Attempt: ${attempt + 1}/${maxRetries + 1}`);

          // Renew JWT and retry once before treating 401 as signed-out
          if (
            response.status === 401 &&
            !isRefreshEndpoint &&
            !this.isPublicAuthEndpoint(endpoint) &&
            !tokenRefreshAttempted
          ) {
            tokenRefreshAttempted = true;
            if (isAdminEndpoint && !!localStorage.getItem('adminToken')) {
              try {
                const refreshResult = await this.refreshAdminToken();
                if (refreshResult.success && refreshResult.token) {
                  processedConfig.headers = {
                    ...(processedConfig.headers as Record<string, string>),
                    Authorization: `Bearer ${refreshResult.token}`,
                  };
                  console.log('API: Admin token refreshed after 401, retrying request');
                  continue;
                }
                console.warn('API: Admin token refresh after 401 unsuccessful:', refreshResult.error);
                if (!(window as any).__sessionRedirectStarted) {
                  this.clearAdminToken();
                  try {
                    const { adminService } = await import('./adminService');
                    adminService.clearAdminSession();
                  } catch {
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminTokenInfo');
                  }
                }
              } catch (refreshError) {
                console.warn('API: Admin token refresh after 401 failed:', refreshError);
                if (!(window as any).__sessionRedirectStarted) {
                  this.clearAdminToken();
                  try {
                    const { adminService } = await import('./adminService');
                    adminService.clearAdminSession();
                  } catch {
                    localStorage.removeItem('adminToken');
                    localStorage.removeItem('adminTokenInfo');
                  }
                }
              }
            } else if (!isAdminEndpoint && !!localStorage.getItem('authToken')) {
              try {
                const { authService } = await import('./authService');
                const newToken = await authService.refreshToken();
                processedConfig.headers = {
                  ...(processedConfig.headers as Record<string, string>),
                  Authorization: `Bearer ${newToken}`,
                };
                console.log('API: User token refreshed after 401, retrying request');
                continue;
              } catch (refreshError) {
                console.warn('API: User token refresh after 401 failed:', refreshError);
              }
            }
          }

          if (isAuthError || (isAuthEndpoint && response.status >= 400 && response.status < 500)) {
            console.error(`API: Authentication error ${response.status}, not retrying`);
            authErrorDetected = true;
            // Process the response to get proper error details and throw immediately
            await this.handleResponse<T>(response);
          }
        }
        const result = await performanceService.measureApiCall(
          `${method}_${endpoint}`,
          () => this.handleResponse<T>(response)
        );

        console.log('API: Processed response result:', result);

        // Log successful API call if not a LogManagement endpoint (fire-and-forget so logging never blocks the response)
        if (this.apiLoggerInitialized && !isLogManagementEndpoint) {
          const responseTime = Date.now() - requestStartTime;
          const requestSize = this.calculateRequestSize(data);
          const responseSize = this.estimateResponseSize(result);
          import('./apiLogger').then(({ apiLogger }) =>
            apiLogger.logSuccessfulRequest(
              method,
              endpoint,
              response.status,
              responseTime,
              requestSize,
              responseSize,
              { source: 'ApiService', category: 'api_request', tags: ['successful-request'] }
            )
          ).catch(() => {});
        }

        // Cache successful GET requests using centralized cache service
        if (method === 'GET' && config?.cache !== false) {
          const ttl = config?.cacheTTL || this.config.cacheTTL;
          cacheService.set(cacheKey, result, ttl);
        }

        return result;
      } catch (error) {
        console.error(`API: Request failed on attempt ${attempt + 1}:`, error);
        lastError = error as Error;

        // If caller aborted (e.g. navigation), don't retry or overwrite error
        if ((error as Error).name === 'AbortError') {
          throw error;
        }

        // If this is an authentication error, don't retry
        if (authErrorDetected) {
          console.error(`API: Authentication error detected, breaking retry loop`);
          throw error;
        }

        // Log failed API call if not a LogManagement endpoint (fire-and-forget so logging never blocks)
        if (this.apiLoggerInitialized && !isLogManagementEndpoint) {
          const responseTime = Date.now() - requestStartTime;
          const statusCode = (error as any).statusCode || 0;
          import('./apiLogger').then(({ apiLogger }) =>
            apiLogger.logFailedRequest(
              method,
              endpoint,
              statusCode,
              error as Error,
              responseTime,
              { source: 'ApiService', category: 'api_error', tags: ['failed-request', `attempt-${attempt + 1}`] }
            )
          ).catch(() => {});
        }

        // Enhanced retry logic for LogManagement endpoints
        if (isLogManagementEndpoint && isLogManagementError(error) && error.name === 'LogManagementAuthError' && !tokenRefreshAttempted) {
          console.log('API: LogManagement auth error, attempting token refresh...');
          tokenRefreshAttempted = true;

          try {
            // Try to refresh admin token
            const refreshResult = await this.refreshLogManagementToken();

            if (refreshResult.success) {
              console.log('API: LogManagement token refresh successful, retrying request...');
              // Update the request config with new token
              processedConfig.headers = {
                ...processedConfig.headers,
                Authorization: `Bearer ${refreshResult.token}`
              };
              // Continue to retry with refreshed token
              continue;
            } else {
              console.log('API: LogManagement token refresh failed, dispatching auth failure event');
              // Dispatch event to trigger admin login redirect
              window.dispatchEvent(new CustomEvent('logmanagement:auth-failed', {
                detail: {
                  error: { statusCode: 401, message: 'Token refresh failed' },
                  endpoint,
                  requiresLogin: true
                }
              }));
              throw error;
            }
          } catch (refreshError) {
            console.error('API: LogManagement token refresh error:', refreshError);
            // Dispatch event to trigger admin login redirect
            window.dispatchEvent(new CustomEvent('logmanagement:auth-failed', {
              detail: {
                error: { statusCode: 401, message: 'Token refresh failed' },
                endpoint,
                requiresLogin: true
              }
            }));
            throw error;
          }
        }

        // Enhanced retry logic for other LogManagement errors
        if (isLogManagementEndpoint && isLogManagementError(error) && 'retryable' in error && error.retryable) {
          console.log(`API: LogManagement retryable error (${error.name}), will retry...`);
          // Continue to retry for retryable errors
        }

        // Don't retry on certain errors
        if (error instanceof Error) {
          if (error.name === 'AbortError') {
            console.error('API: Request was aborted due to timeout');
            throw new Error('Request timed out - please check your connection and try again');
          }

          // Handle network connectivity issues
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || 
              error.name === 'AbortError' || error.message.includes('aborted')) {
            console.error('API: Network connectivity issue detected');
            console.error('API: Full error details:', {
              message: error.message,
              name: error.name,
              stack: error.stack,
              url: url,
              method: method,
              attempt: attempt + 1
            });

            if (!navigator.onLine) {
              throw new Error('No internet connection - please check your network and try again');
            } else if (error.name === 'AbortError' || error.message.includes('aborted')) {
              // Timeout or aborted request - provide more helpful message
              const isLoginEndpoint = url.includes('/auth/login') || url.includes('/Auth/login');
              if (isLoginEndpoint && attempt >= maxRetries) {
                throw new Error('Login request timed out. The server may be slow or unreachable. Please check your connection and try again.');
              } else {
                throw new Error('Request timed out - please check your connection and try again');
              }
            } else {
              // Check if this might be a CORS issue
              const corsKeywords = ['cors', 'cross-origin', 'preflight', 'access-control'];
              const isCorsError = corsKeywords.some(keyword =>
                error.message.toLowerCase().includes(keyword)
              );

              if (isCorsError) {
                throw new Error(`CORS Error: The backend server at ${this.config.baseURL} is not configured to allow requests from ${window.location.origin}. Please configure CORS on your backend server.`);
              } else {
                // Provide more specific error message for debugging
                const isLoginEndpoint = url.includes('/auth/login') || url.includes('/Auth/login');
                if (isLoginEndpoint) {
                  throw new Error(`Unable to connect to the server. Please check if the backend server is running on ${this.config.baseURL} and try again.`);
                } else {
                  throw new Error(`Network error: ${error.message}. URL: ${url}. Please check if the backend server is running on ${this.config.baseURL}`);
                }
              }
            }
          }

          // Don't retry on client errors except for specific LogManagement cases
          if (isLogManagementError(error)) {
            // LogManagement errors have their own retry logic above
            if (!error.retryable && error.name !== 'LogManagementAuthError') {
              console.error(`API: Non-retryable LogManagement error (${error.name}), not retrying`);
              throw error;
            }
          } else {
            // Authentication errors are now handled before response processing
            // This catch block handles other types of errors

            // Check for other client errors (4xx) and server errors (5xx)
            if ('statusCode' in (error as any) && typeof (error as any).statusCode === 'number') {
              const statusCode = (error as any).statusCode as number;
              // Don't retry on client errors (4xx) except for timeout and rate limit
              if (statusCode >= 400 && statusCode < 500 && statusCode !== 408 && statusCode !== 429) {
                console.error(`API: Client error ${statusCode}, not retrying`);
                throw error;
              }
              // Don't retry on server errors (5xx) - they indicate server-side issues
              if (statusCode >= 500 && statusCode < 600) {
                console.error(`API: Server error ${statusCode}, not retrying (server-side issue)`);
                throw error;
              }
            }
          }
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          console.log(`API: Waiting ${delay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    console.error('API: All retry attempts failed');
    throw lastError!;
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.makeRequest<T>('GET', endpoint, undefined, config);
  }

  async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.makeRequest<T>('POST', endpoint, data, config);
  }

  async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    console.log('ApiService: PUT request initiated for endpoint:', endpoint);
    console.log('ApiService: PUT request data:', data);
    console.log('ApiService: Current token:', this.token ? 'Token present' : 'No token');
    return this.makeRequest<T>('PUT', endpoint, data, config);
  }

  async patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.makeRequest<T>('PATCH', endpoint, data, config);
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.makeRequest<T>('DELETE', endpoint, undefined, config);
  }

  // Utility methods
  clearCache(): void {
    cacheService.clear();
  }

  getCacheSize(): number {
    return cacheService.getStats().size;
  }

  getCacheStats() {
    return cacheService.getStats();
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Update API base URL dynamically (for admin configuration)
   */
  updateBaseURL(newBaseURL: string): void {
    // Update through centralized config manager
    apiConfig.setBaseUrl(newBaseURL);
    console.log('ApiService: Base URL updated through central config to:', newBaseURL);
  }

  /**
   * Get current API base URL
   */
  getBaseURL(): string {
    return this.config.baseURL;
  }

  /**
   * Force reset API URL to default and clear all caches
   */
  forceResetToDefault(): void {
    // Reset through centralized config manager
    apiConfig.resetToDefault();

    // Clear all caches
    this.clearCache();

    console.log('ApiService: Force reset to default through central config');
  }

  /**
   * Check if an endpoint is a LogManagement endpoint
   */
  isLogManagementEndpoint(endpoint: string): boolean {
    return endpoint.includes('/api/LogManagement/');
  }

  /**
   * Get the current admin token for LogManagement operations
   * FIXED: Uses reliable token storage service
   */
  getAdminToken(): string | null {
    // Import adminTokenStorage dynamically to avoid circular dependencies
    try {
      const { adminTokenStorage } = require('./adminTokenStorage');
      return adminTokenStorage.getToken();
    } catch (error) {
      console.warn('ApiService: Failed to get admin token from storage service, falling back to localStorage');
      return localStorage.getItem('adminToken');
    }
  }

  /**
   * Check if admin token is valid and not expired
   * FIXED: Uses reliable token storage service
   */
  isAdminTokenValid(): boolean {
    try {
      const { adminTokenStorage } = require('./adminTokenStorage');
      return adminTokenStorage.isAuthenticated();
    } catch (error) {
      console.warn('ApiService: Failed to check admin token validity from storage service, falling back to localStorage');
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken) return false;

      const adminTokenInfo = localStorage.getItem('adminTokenInfo');
      if (!adminTokenInfo) return false;

      try {
        const tokenInfo = JSON.parse(adminTokenInfo);
        if (tokenInfo.expiresAt) {
          const expirationTime = new Date(tokenInfo.expiresAt).getTime();
          if (Date.now() >= expirationTime) {
            return false;
          }
        }
        return true;
      } catch {
        return false;
      }
    }
  }

  /**
   * Force refresh admin token for LogManagement operations
   */
  async forceRefreshAdminToken(): Promise<boolean> {
    try {
      const refreshResult = await this.refreshLogManagementToken();
      return refreshResult.success;
    } catch (error) {
      console.error('API: Force refresh admin token failed:', error);
      return false;
    }
  }

  /**
   * Make an admin request with automatic token validation and restoration
   * Requirement 1.4, 2.4: Automatically restore token and ensure valid authentication
   */
  async makeAdminRequest<T>(
    method: string,
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    try {
      console.log(`ApiService: Making admin request: ${method} ${endpoint}`);
      
      // Ensure we have a valid admin token before making the request
      const hasValidToken = await this.ensureAdminToken();
      
      if (!hasValidToken) {
        console.error('ApiService: No valid admin token available for request');
        
        // Dispatch event to trigger login
        window.dispatchEvent(new CustomEvent('admin:auth-required', {
          detail: { 
            endpoint,
            method,
            timestamp: Date.now()
          }
        }));
        
        throw new Error('Admin authentication required');
      }
      
      // Make the request using the standard method
      return await this.makeRequest<T>(method, endpoint, data, config);
      
    } catch (error) {
      console.error(`ApiService: Admin request failed: ${method} ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * Admin-specific HTTP methods with automatic token validation
   */
  async adminGet<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.makeAdminRequest<T>('GET', endpoint, undefined, config);
  }

  async adminPost<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.makeAdminRequest<T>('POST', endpoint, data, config);
  }

  async adminPut<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.makeAdminRequest<T>('PUT', endpoint, data, config);
  }

  async adminPatch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    return this.makeAdminRequest<T>('PATCH', endpoint, data, config);
  }

  async adminDelete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.makeAdminRequest<T>('DELETE', endpoint, undefined, config);
  }

  /**
   * Calculate request size for logging
   */
  private calculateRequestSize(data: any): number {
    if (!data) return 0;
    if (typeof data === 'string') return data.length;
    if (data instanceof FormData) return 0; // Can't easily calculate FormData size
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  /**
   * Estimate response size for logging
   */
  private estimateResponseSize(data: any): number {
    if (!data) return 0;
    try {
      return JSON.stringify(data).length;
    } catch {
      return 0;
    }
  }

  /**
   * Manually initialize API logging (useful for testing or delayed initialization)
   */
  async initializeLogging(): Promise<void> {
    if (!this.apiLoggerInitialized) {
      await this.initializeApiLogging();
    }
  }

  /**
   * Check if API logging is initialized
   */
  isLoggingInitialized(): boolean {
    return this.apiLoggerInitialized;
  }

  /**
   * Get API logging statistics
   */
  async getLoggingStatistics(): Promise<any> {
    if (!this.apiLoggerInitialized) {
      return { error: 'API logging not initialized' };
    }

    try {
      const { apiLogger } = await import('./apiLogger');
      return apiLogger.getStatistics();
    } catch (error) {
      return { error: 'Failed to get logging statistics' };
    }
  }

  // File upload method
  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>,
    onProgress?: (progress: number) => void
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();

      formData.append('file', file);
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch {
            resolve(xhr.responseText as any);
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('POST', `${this.config.baseURL}${endpoint}`);

      if (this.token) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      }

      xhr.send(formData);
    });
  }
}

export const apiService = new ApiService();